import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ICustomer } from '../interfaces/customer';
import { IRegion } from '../interfaces/region';
import { ISupplier } from '../interfaces/supplier';
import { ISupplierFilter } from '../interfaces/supplier-filter';
import { EditService } from './edit.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import { ISupplierDetailedInfo } from '../interfaces/telephony/supplier-detailed-info';
import { ISupplierRegionFilter } from '../interfaces/supplier-region-filter';
import moment from 'moment';
import { compare } from 'fast-json-patch';

@Injectable({ providedIn: 'root' })
export class SupplierService extends EditService<ISupplier, ISupplierFilter> {
  url = '/api/supplier';
  entityTitle = 'Supplier';
  public selected_org_id = 0;

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  GetCurrent(): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/getcurrent'),
      this.handlersFactory.getForReadAll(),
    );
  }

  saveSupplierForm(data: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/saveform', data),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  getAllForTagging(): Promise<any[]> {
    return this.handleRequest<any[]>(
      this.http.get<any[]>(`${this.url}/allForTagging`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getTypes(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/gettypes'),
      this.handlersFactory.getForReadAll(),
    );
  }

  getRegions(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/supplier/getRegions'),
      this.handlersFactory.getForReadAll(),
    );
  }

  patch(supplierId: number, after: any, before: any = {}): Promise<any> {
    const patch = compare(before, after);

    return this.handleRequest(
      this.http.patch('api/supplier/' + supplierId, patch),
      this.handlersFactory.getForUpdate('Details'),
    );
  }

  getRegionsForSupplier(supplierId: number): Promise<IRegion[]> {
    return this.handleRequest(
      this.http.get<IRegion[]>('api/supplier/getRegionsForSupplier/' + supplierId),
      this.handlersFactory.getForReadAll(),
    );
  }

  getRegionsForSuppliers(filters: ISupplierRegionFilter): Promise<IRegion[]> {
    return this.handleRequest(
      this.http.get<IRegion[]>('api/supplier/regions', {
        params: this.constructHttpParams(filters),
      }),
      this.handlersFactory.getForReadAll(),
    );
  }

  getDepots(regionId: number): Promise<any> {
    return this.handleRequest(
      this.http.get('api/supplier/getDepots?regionId=' + regionId),
      this.handlersFactory.getForReadAll(),
    );
  }

  getAll(taskTypeId?: number): Promise<ICustomer[]> {
    const params = this.constructHttpParams({ taskTypeId: taskTypeId });
    return this.handleRequest(
      this.http.get<ICustomer[]>('api/supplier/getAll', { params: params }),
      this.handlersFactory.getForReadAll(),
    );
  }

  getByERegions(eRegions: number[], taskTypeId?: number): Promise<ICustomer[]> {
    const params = this.constructHttpParams({ taskTypeId: taskTypeId });
    return this.handleRequest(
      this.http.post<ICustomer[]>('api/supplier/getbyeregions', eRegions, { params: params }),
      this.handlersFactory.getForReadAll(),
    );
  }

  getDirectedSuppliers(jobId): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>('api/supplier/directedsuppliers/' + jobId),
      this.handlersFactory.getForReadAll(),
    );
  }

  getJobsByMonthThisYear(id): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/${id}/graphs/jobs-by-month-this-year`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getDeactivatedSuppliers(): Promise<any> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/getDeactivatedSuppliers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getPreventedSuppliers(): Promise<any> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/getPreventedSuppliers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getPreWarnedSuppliers(): Promise<any> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/getPreWarnedSuppliers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getDeactivationSettings(): Promise<any> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/getDeactivationSettings`),
      this.handlersFactory.getForReadAll(),
    );
  }

  saveDeactivationSettings(data: any): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    return this.handleRequest(
      this.http.post(this.url + '/saveDeactivationSettings', data, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Supplier Settings Saved',
    );
  }

  getDeactivationCSV() {
    let filename = 'DeactivatedSuppliers_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';
    let report = this.http.get(`${this.url}/getDeactivationCSV`, { responseType: 'blob' });

    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Unable to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  getPreventedCSV() {
    let filename = 'PreventedSuppliers_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';
    let report = this.http.get(`${this.url}/getPreventedCSV`, { responseType: 'blob' });

    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Unable to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  getPreWarnedCSV() {
    let filename = 'PreWarnedSuppliers_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';
    let report = this.http.get(`${this.url}/getPreWarnedCSV`, { responseType: 'blob' });

    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Unable to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  getDeactivationReasons(): Promise<any> {
    return this.http.get<any>(`${this.url}/getDeactivationReasons`).toPromise();
  }

  getSuspensionReasons(): Promise<any> {
    return this.http.get<any>(`${this.url}/suspension-reasons`).toPromise();
  }

  getSupplierClassifications() {
    return this.http.get<any>(`${this.url}/supplierClassifications`).toPromise();
  }

  getDetailedInformationFromNumber(phoneNumber: string): Promise<ISupplierDetailedInfo> {
    return this.http
      .get<ISupplierDetailedInfo>(`${this.url}/detailedInfoFromNumber/${phoneNumber}`)
      .toPromise();
  }

  getDetailedInformationFromId(id: number): Promise<ISupplierDetailedInfo> {
    return this.http.get<ISupplierDetailedInfo>(`${this.url}/detailedInfoFromId/${id}`).toPromise();
  }
}
